import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, switchMap, take, finalize } from 'rxjs/operators';
import { AuthService } from 'src/app/modules/auth/services/auth.service';
import { Router } from '@angular/router';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  // Endpoints yang gak perlu auto-refresh token
  private skipAuthUrls = [
    '/auth/login',
    '/auth/register',
    '/auth/otp',
    '/auth/otp/verify',
    '/auth/change-password',
    '/auth/refresh-token',
    '/web/webinar',
    '/web/webinar/detail',
    '/web/program/detail',
    '/web/home',
  ];

  constructor(private authService: AuthService, private router: Router) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const loginData = this.authService.getStoredLoginData();
    const token = loginData?.token;

    // Selalu inject token fresh (override kalau perlu)
    let authReq = req;
    if (token) {
      authReq = req.clone({
        setHeaders: { Authorization: 'Bearer ' + token },
      });
    }

    return next.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        if (token && (error.status === 401 || error.status === 403) && !this.shouldSkipAuth(req.url)) {
          return this.handle401Error(authReq, next);
        }
        return throwError(() => error);
      })
    );
  }

  private shouldSkipAuth(url: string): boolean {
    return this.skipAuthUrls.some((skipUrl) => url.includes(skipUrl));
  }

  private handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.authService.refreshToken().pipe(
        switchMap((response) => {
          if (response?.data?.token) {
            const newToken = response.data.token;
            const newRefreshToken = response.data.refreshToken;
            this.authService.updateToken(newToken, newRefreshToken);
            this.refreshTokenSubject.next(newToken);
            return next.handle(this.addTokenHeader(req, newToken));
          }
          return this.logoutAndReturn();
        }),
        catchError(() => {
          return this.logoutAndReturn();
        }),
        finalize(() => {
          this.isRefreshing = false;
        })
      );
    } else {
      // Tunggu refresh selesai, lalu retry dengan token baru
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) => {
          return next.handle(this.addTokenHeader(req, token!));
        })
      );
    }
  }

  private addTokenHeader(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({
      setHeaders: { Authorization: 'Bearer ' + token },
    });
  }

  private logoutAndReturn(): Observable<HttpEvent<any>> {
    this.authService.logOut();
    return throwError(() => new Error('Session expired. Please login again.'));
  }
}
